import { Component, input, output, signal, HostListener, ElementRef, ViewChild, AfterViewInit, OnDestroy, effect } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-dialog',
  imports: [CommonModule],
  template: `
    @if (_visible()) {
      <div 
        class="fixed inset-0 z-[9999] flex items-center justify-center p-4"
        [class]="backdropClasses"
        (click)="onBackdropClick()">
        <div
          #dialogElement
          [class]="dialogClasses"
          [attr.role]="'dialog'"
          [attr.aria-modal]="true"
          [attr.aria-labelledby]="headerId()"
          [attr.aria-describedby]="contentId()"
          (click)="$event.stopPropagation()"
          (keydown.escape)="onEscape()">
          @if (showHeader()) {
            <div [id]="headerId()" class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100">{{ header() }}</h2>
              @if (closable()) {
                <button
                  type="button"
                  (click)="close()"
                  class="p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  aria-label="Close dialog">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              }
            </div>
          }
          <div [id]="contentId()" [class]="contentClasses">
            <ng-content></ng-content>
          </div>
          @if (showFooter()) {
            <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3">
              <ng-content select="[footer]"></ng-content>
            </div>
          }
        </div>
      </div>
    }
  `,
  styles: []
})
export class DialogComponent implements AfterViewInit, OnDestroy {
  visible = input<boolean>(false);
  header = input<string>('');
  showHeader = input<boolean>(true);
  showFooter = input<boolean>(true);
  closable = input<boolean>(true);
  dismissableMask = input<boolean>(true);
  modal = input<boolean>(true);
  position = input<'center' | 'top' | 'bottom' | 'left' | 'right'>('center');
  width = input<string>('auto');
  height = input<string>('auto');
  maximizable = input<boolean>(false);
  blockScroll = input<boolean>(true);

  visibleChange = output<boolean>();
  closeEvent = output<void>();
  
  _visible = signal(this.visible());

  @ViewChild('dialogElement', { static: false }) dialogElement!: ElementRef<HTMLElement>;

  isMaximized = signal(false);
  headerId = signal(`dialog-header-${Math.random().toString(36).substr(2, 9)}`);
  contentId = signal(`dialog-content-${Math.random().toString(36).substr(2, 9)}`);

  constructor() {
    // Watch for changes to visible input
    effect(() => {
      this._visible.set(this.visible());
      if (this.visible() && this.blockScroll()) {
        document.body.style.overflow = 'hidden';
      } else if (!this.visible() && this.blockScroll()) {
        document.body.style.overflow = '';
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.dialogElement && this._visible()) {
      this.dialogElement.nativeElement.focus();
    }
  }

  ngOnDestroy(): void {
    if (this.blockScroll()) {
      document.body.style.overflow = '';
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this._visible() && this.closable()) {
      this.close();
    }
  }

  close(): void {
    this._visible.set(false);
    this.visibleChange.emit(false);
    this.closeEvent.emit();
    if (this.blockScroll()) {
      document.body.style.overflow = '';
    }
  }

  onBackdropClick(): void {
    if (this.dismissableMask() && this.modal()) {
      this.close();
    }
  }

  toggleMaximize(): void {
    this.isMaximized.set(!this.isMaximized());
  }

  get backdropClasses(): string {
    return 'bg-black/50 dark:bg-black/70 backdrop-blur-sm';
  }

  get dialogClasses(): string {
    const classes = [
      'bg-white',
      'dark:bg-gray-800',
      'rounded-lg',
      'shadow-xl',
      'max-w-full',
      'max-h-[90vh]',
      'overflow-hidden',
      'flex',
      'flex-col',
      'focus:outline-none'
    ];

    if (this.isMaximized()) {
      classes.push('w-screen', 'h-screen', 'max-w-none', 'max-h-none', 'rounded-none');
    } else {
      if (this.width() !== 'auto') {
        classes.push(`w-${this.width()}`);
      }
      if (this.height() !== 'auto') {
        classes.push(`h-${this.height()}`);
      }
    }

    const positionMap = {
      center: 'items-center justify-center',
      top: 'items-start justify-center pt-8',
      bottom: 'items-end justify-center pb-8',
      left: 'items-center justify-start pl-8',
      right: 'items-center justify-end pr-8'
    };
    classes.push(positionMap[this.position()]);

    return classes.join(' ');
  }

  get contentClasses(): string {
    return 'px-6 py-4 overflow-y-auto flex-1';
  }
}

